// Plan 24c Phase B, Task 4 — Error Modal screen (Screen 27)
//
// Centered modal with a red accent stripe, displayed when the router navigates
// to 'error-modal' with an ErrorModalPayload.  Three variants:
//   - room-not-found: warns the player that the room code doesn't exist / expired
//   - room-full:      informs the player that the room is at capacity
//   - connection-failed: reports a server connectivity error; offers Retry + Back to Warmup

import { createModal } from '@client/ui/components/modal';
import type { ModalHandle } from '@client/ui/components/modal';
import { createButton } from '@client/ui/components/button';
import type { UIComponentHandle } from '@client/ui/components/types';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type ErrorModalVariant = 'room-not-found' | 'room-full' | 'connection-failed';

export type ErrorModalPayload = {
  variant: ErrorModalVariant;
  details?: { roomCode?: string };
  onPrimary?: () => void;
  onSecondary?: () => void; // only used by 'connection-failed' variant's Back to Warmup button
};

// ─────────────────────────────────────────────────────────────────────────────
// Variant content map
// ─────────────────────────────────────────────────────────────────────────────

interface VariantContent {
  title: string;
  message: (details?: { roomCode?: string }) => string;
}

const VARIANT_CONTENT: Record<ErrorModalVariant, VariantContent> = {
  'room-not-found': {
    title: 'Room Not Found',
    message: (details) =>
      `The room code ${details?.roomCode ?? 'XYZW'} does not exist or has expired.`,
  },
  'room-full': {
    title: 'Room Full',
    message: () => 'This room already has 8 players.',
  },
  'connection-failed': {
    title: 'Connection Failed',
    message: () => 'Could not connect to the server. Please check your internet connection.',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createErrorModalScreen(ctx: ScreenMountContext<ErrorModalPayload>): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { variant, details, onPrimary, onSecondary } = payload;

  let disposed = false;

  // Collected button handles for disposal
  const buttonHandles: UIComponentHandle[] = [];

  // Resolve variant content
  const content = VARIANT_CONTENT[variant];
  const titleText = content.title;
  const messageText = content.message(details);

  // Default primary action — return to the warmup arena.
  const defaultPrimary = (): void => {
    router.navigate('warmup-arena-overlay', { portal: null });
  };

  // ── Build buttons (DOM elements for createModal's `buttons` array) ──────────
  //
  // createModal expects pre-built HTMLElement[]. We call createButton WITHOUT
  // a `mount` so it returns the element but doesn't append it yet — createModal
  // appends them into its buttons container.
  // We keep the UIComponentHandle for disposal.

  const buttonElements: HTMLElement[] = [];

  if (variant === 'connection-failed') {
    // Primary: Retry
    const retryHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'primary',
      size: 'default',
      label: 'Retry',
      onClick: () => {
        if (!disposed) {
          if (onPrimary) {
            onPrimary();
          } else {
            defaultPrimary();
          }
        }
      },
    });
    buttonHandles.push(retryHandle);
    buttonElements.push(retryHandle.element);

    // Secondary: Back to Warmup (Ghost)
    const backHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'ghost',
      size: 'default',
      label: 'Back to Warmup',
      onClick: () => {
        if (!disposed) {
          if (onSecondary) {
            onSecondary();
          } else {
            defaultPrimary();
          }
        }
      },
    });
    buttonHandles.push(backHandle);
    buttonElements.push(backHandle.element);
  } else if (variant === 'room-full') {
    // Primary: OK
    const okHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'primary',
      size: 'default',
      label: 'OK',
      onClick: () => {
        if (!disposed) {
          if (onPrimary) {
            onPrimary();
          } else {
            defaultPrimary();
          }
        }
      },
    });
    buttonHandles.push(okHandle);
    buttonElements.push(okHandle.element);
  } else {
    // room-not-found — Primary: Back to Warmup
    const backHandle = createButton({
      gsapRegistry,
      mount: undefined,
      variant: 'primary',
      size: 'default',
      label: 'Back to Warmup',
      onClick: () => {
        if (!disposed) {
          if (onPrimary) {
            onPrimary();
          } else {
            defaultPrimary();
          }
        }
      },
    });
    buttonHandles.push(backHandle);
    buttonElements.push(backHandle.element);
  }

  // ── Create modal ────────────────────────────────────────────────────────────

  let modalHandle: ModalHandle | null = createModal({
    gsapRegistry,
    mount: host,
    accentType: 'error',
    title: titleText,
    message: messageText,
    buttons: buttonElements,
  });

  // Show immediately (animate in)
  modalHandle.show();

  const root = modalHandle.element;

  // ── ESC handler ────────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (!disposed) {
      if (onPrimary) {
        onPrimary();
      } else {
        defaultPrimary();
      }
    }
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);

    // Dispose all button handles
    for (const handle of buttonHandles) {
      handle.dispose();
    }

    // Dispose modal handle
    if (modalHandle) {
      modalHandle.dispose();
      modalHandle = null;
    }
  }

  return { element: root, dispose };
}
